import { useCallback, useContext, useEffect, useState } from "react";
import BryntumContext from "@/react/context/bryntumContext";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { isIAmOneOfBoardOwner } from "@/other/utils/utils";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

/**
 * Custom hook to check board-level permissions for settings
 *
 * Returns permission flags for the current user:
 * - canRead: User has scheduler+ role (required to view settings)
 * - canWrite: User is board owner and not a demo user (required to modify settings)
 * - isBoardOwner: User is one of the board owners
 * - canManageSecrets: Owner or board Admin (G1), excluding demo users. CompanyCam and OpenAI keys.
 * - canManageCompanyCam: canManageSecrets and not a sandbox board.
 *
 * @returns Object containing permission flags
 */
export const useBoardPermissions = () => {
  const { user, project, boardManager, selectedBoard } = useContext(BryntumContext);

  const resolvePermissions = useCallback(() => {
    const canRead = user?.can(GRANTS.grant_three); // scheduler+

    // Permission check: board owner only, excluding demo users
    // @ts-expect-error - boardManager is dynamically set in bryntumContext
    const boardStore = boardManager?.boardNameStore?.getById(selectedBoard?.id);
    const isBoardOwner = project && boardStore ? isIAmOneOfBoardOwner(project, boardStore.owner) : false;
    const canWrite = isBoardOwner && !user?.isDemoUser;
    const isBoardAdmin = Boolean(user?.can(GRANTS.grant_one));
    const canManageSecrets = (isBoardOwner || isBoardAdmin) && !user?.isDemoUser;
    // @ts-expect-error - user.isSandboxBoard is dynamically typed
    const canManageCompanyCam = canManageSecrets && !user?.isSandboxBoard;

    return {
      canRead,
      canWrite,
      isBoardOwner,
      canManageSecrets,
      canManageCompanyCam,
    };
  }, [user, project, boardManager, selectedBoard?.id]);

  const [permissions, setPermissions] = useState(() => resolvePermissions());

  useEffect(() => {
    setPermissions(resolvePermissions());
  }, [resolvePermissions]);

  useSwivuStore({
    storeId: STORE_KEYS.boardUserStore,
    subscriberFn: () => {
      setPermissions(resolvePermissions());
      return false;
    },
  });

  return permissions;
};
